import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Flex,
  Badge,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaRocket, FaCoins, FaChartLine, FaChartBar, FaServer, FaBolt, FaNetworkWired, FaShieldAlt, FaCheckCircle, FaCubes, FaMicrochip, FaRobot, FaRandom } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../components/Footer';

const Home = () => {
  const bgColor = useColorModeValue('gray.800', 'gray.900');
  const cardBg = useColorModeValue('gray.700', 'gray.800');

  const features = [
    {
      icon: FaRobot,
      title: 'AI-Controlled',
      description: 'Rewards are controlled and audited by AI for fairness and transparency.',
    },
    {
      icon: FaRandom,
      title: 'Random Selection',
      description: '5-10 wallets chosen randomly each day to receive SOL rewards.',
    },
    {
      icon: FaChartLine,
      title: 'Volume-Based',
      description: 'Higher trading volume means bigger rewards for selected holders.',
    },
  ];

  const blockchainFeatures = [
    {
      icon: FaServer,
      title: 'Internet Computer (ICP)',
      description: 'Built on the Internet Computer, the world\'s fastest and most scalable blockchain. ICP offers unlimited scalability, low latency, and high security for our platform.',
    },
    {
      icon: FaBolt,
      title: 'Solana Integration',
      description: 'Leveraging Solana\'s high-performance blockchain for fast transactions and low fees. Solana provides the speed and efficiency needed for seamless token operations.',
    },
  ];

  const integrationFeatures = [
    {
      icon: FaNetworkWired,
      title: 'Cross-Chain Innovation',
      description: 'Our unique integration of ICP and Solana creates a powerful synergy. ICP provides the backend infrastructure and smart contract execution, while Solana handles the token operations and rewards distribution.',
    },
    {
      icon: FaShieldAlt,
      title: 'Enhanced Security',
      description: 'By leveraging both ICP\'s secure canister system and Solana\'s robust validator network, we create multiple layers of security for your assets and transactions.',
    },
  ];

  const icpPowerFeatures = [
    {
      icon: FaCubes,
      title: 'Canister Architecture',
      description: 'ICP\'s canister architecture allows for modular, upgradable smart contracts that can be deployed and updated without downtime.',
    },
    {
      icon: FaMicrochip,
      title: 'WebAssembly Execution',
      description: 'Smart contracts run on WebAssembly, providing near-native performance and enabling complex computations with minimal cost.',
    },
  ];

  return (
    <Box pt={20} minH="100vh" bg={bgColor}>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" mb={10}>
            <Heading
              as="h1"
              size="2xl"
              bgGradient="linear(to-r, cyan.400, blue.500)"
              bgClip="text"
              mb={4}
            >
              HODL Protocol
            </Heading>
            <Text fontSize="xl" color="gray.400" mb={8}>
              AI-controlled rewards for token holders
            </Text>
            <Button
              as={RouterLink}
              to="/how-it-works"
              colorScheme="cyan"
              size="lg"
              leftIcon={<FaRocket />}
            >
              Get Started
            </Button>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={10}>
            {features.map((feature, index) => (
              <Box
                key={index}
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="xl"
                transition="transform 0.2s"
                _hover={{ transform: 'translateY(-5px)' }}
              >
                <VStack spacing={4} align="start">
                  <Icon as={feature.icon} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="white">
                    {feature.title}
                  </Heading>
                  <Text color="gray.400">{feature.description}</Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Moonit Launch
            </Heading>
            <Text color="gray.400" mb={6}>
              The HODL token will be launched on Moonit using a bonding curve mechanism. This innovative approach ensures fair token distribution and price discovery.
            </Text>
            <Text color="gray.400" mb={6}>
              <strong>Important:</strong> Rewards will begin 24 hours after the token meets the Moonit bonding curve criteria. This waiting period ensures stability and proper token distribution before rewards commence.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Box>
                <Icon as={FaChartBar} w={8} h={8} color="cyan.400" mb={4} />
                <Heading size="md" color="white" mb={2}>
                  Fair Launch
                </Heading>
                <Text color="gray.400">
                  Token distribution through Moonit's bonding curve ensures fair pricing and access for all participants.
                </Text>
              </Box>
              <Box>
                <Icon as={FaRobot} w={8} h={8} color="cyan.400" mb={4} />
                <Heading size="md" color="white" mb={2}>
                  AI Rewards
                </Heading>
                <Text color="gray.400">
                  AI selects 5-10 random wallets daily to receive SOL rewards based on trading volume.
                </Text>
              </Box>
              <Box>
                <Icon as={FaChartLine} w={8} h={8} color="cyan.400" mb={4} />
                <Heading size="md" color="white" mb={2}>
                  Long-Term Benefits
                </Heading>
                <Text color="gray.400">
                  The longer you hold, the more chances you have of receiving multiple rewards over time.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Powered by Advanced Blockchains
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {blockchainFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              The Power of ICP & Solana Integration
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {integrationFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
            <Box mt={6}>
              <Text color="gray.400">
                The Internet Computer (ICP) provides our platform with:
              </Text>
              <List spacing={3} mt={4} color="gray.400">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Reverse-gas model for seamless user experience
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  WebAssembly-based smart contracts with superior performance
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Chain-key cryptography for enhanced security
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Autonomous smart contracts with continuous operation
                </ListItem>
              </List>
            </Box>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              The Power of Internet Computer
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {icpPowerFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
            <Box mt={6}>
              <Text color="gray.400">
                The Internet Computer revolutionizes blockchain technology with:
              </Text>
              <List spacing={3} mt={4} color="gray.400">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Sub-second finality for instant transaction confirmation
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Unlimited scalability with no gas fees for end users
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Interoperability with other blockchains through Chain Key cryptography
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Advanced governance with Network Nervous System (NNS)
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Built-in identity and authentication systems
                </ListItem>
              </List>
            </Box>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Why Choose HODL?
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Box>
                <Heading size="md" color="white" mb={4}>
                  For Token Holders
                </Heading>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FaCoins} color="cyan.400" />
                    <Text color="gray.400">Earn SOL rewards automatically</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaChartLine} color="cyan.400" />
                    <Text color="gray.400">Compound your earnings</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaRocket} color="cyan.400" />
                    <Text color="gray.400">No complex setup required</Text>
                  </HStack>
                </VStack>
              </Box>
              <Box>
                <Heading size="md" color="white" mb={4}>
                  Platform Benefits
                </Heading>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FaChartBar} color="cyan.400" />
                    <Text color="gray.400">Fair token distribution</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCoins} color="cyan.400" />
                    <Text color="gray.400">Transparent reward system</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaRocket} color="cyan.400" />
                    <Text color="gray.400">Community-driven growth</Text>
                  </HStack>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default Home; 